import { distance, lerp, type Vec2 } from "./math";

export interface Trail {
  readonly points: readonly Vec2[];
}

export const createTrail = (position: Vec2): Trail => ({ points: [position] });

export const trailLength = (trail: Trail): number => {
  let total = 0;
  for (let index = 1; index < trail.points.length; index += 1) {
    const previous = trail.points[index - 1];
    const current = trail.points[index];
    if (previous && current) total += distance(previous, current);
  }
  return total;
};

const trimTrail = (
  points: readonly Vec2[],
  maximumDistance: number,
): Vec2[] => {
  if (points.length < 2) return [...points];
  let remaining = Math.max(0, maximumDistance);
  const kept: Vec2[] = [];
  const newest = points.at(-1);
  if (!newest) return [];
  kept.push(newest);

  for (let index = points.length - 2; index >= 0 && remaining > 0; index -= 1) {
    const newer = points[index + 1];
    const older = points[index];
    if (!newer || !older) continue;
    const segmentLength = distance(newer, older);
    if (segmentLength <= remaining) {
      kept.push(older);
      remaining -= segmentLength;
      continue;
    }
    kept.push(lerp(newer, older, remaining / segmentLength));
    remaining = 0;
  }

  return kept.reverse();
};

export const extendTrail = (
  trail: Trail,
  nextPosition: Vec2,
  maximumDistance: number,
): Trail => {
  const newest = trail.points.at(-1);
  if (!newest) return createTrail(nextPosition);
  const points =
    distance(newest, nextPosition) <= Number.EPSILON
      ? [...trail.points]
      : [...trail.points, nextPosition];
  return { points: trimTrail(points, maximumDistance) };
};

export const sampleTrail = (trail: Trail, spacing: number): Vec2[] => {
  if (!Number.isFinite(spacing) || spacing <= 0)
    throw new Error("trail spacing must be positive");
  const newest = trail.points.at(-1);
  if (!newest) return [];

  const samples = [newest];
  let targetDistance = spacing;
  let traversed = 0;
  for (let index = trail.points.length - 1; index > 0; index -= 1) {
    const segmentEnd = trail.points[index];
    const segmentStart = trail.points[index - 1];
    if (!segmentEnd || !segmentStart) continue;
    const segmentLength = distance(segmentEnd, segmentStart);
    while (targetDistance <= traversed + segmentLength + 1e-9) {
      const amount =
        segmentLength === 0 ? 0 : (targetDistance - traversed) / segmentLength;
      samples.push(lerp(segmentEnd, segmentStart, amount));
      targetDistance += spacing;
    }
    traversed += segmentLength;
  }

  const oldest = trail.points[0];
  const lastSample = samples.at(-1);
  if (oldest && lastSample && distance(oldest, lastSample) > 1e-8)
    samples.push(oldest);
  return samples;
};
